"use client";

import { ComponentType, useEffect, useState } from "react";
import { enGB, fr, nl } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import { useFormContext } from "react-hook-form";

import InputContainer from "@/components/forms/controls/InputContainer";
import { cn } from "@/helpers/className";
import useFormError from "@/hooks/useFormError";

import "react-datepicker/dist/react-datepicker.css";

import Icon from "@/components/atoms/Icon";
import { getMonthNames } from "@/helpers/date";
import { AllowedLanguages } from "@/types/generic";

registerLocale("nl", nl);
registerLocale("en", enGB);
registerLocale("fr", fr);

interface BirthDayInputProps {
  className?: string;
  disabled?: boolean;
  id?: string;
  Icon?: ComponentType;
  hiddenLabel?: boolean;
  initialValue?: Date;
  inputClassname?: string;
  label: string;
  labelClasses?: string;
  locale: AllowedLanguages;
  name: string;
  placeholder?: string;
  required?: boolean;
  errorAfterInput?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
}

const BirthDayInput = ({
  label,
  hiddenLabel = false,
  name,
  labelClasses,
  inputClassname,
  initialValue,
  className,
  locale,
  placeholder = "dd/mm",
  ...props
}: BirthDayInputProps) => {
  const months = getMonthNames(locale);

  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialValue ?? null,
  );

  const error = useFormError(name, errors);
  const id = props.id ?? name;
  const { onBlur: handleBlur } = register(name);

  const getMonth = (date: Date) => {
    return date.getMonth();
  };

  useEffect(() => {
    if (initialValue) setSelectedDate(initialValue);
  }, [initialValue]);

  return (
    <InputContainer className={className} isError={Boolean(error)}>
      <label
        className={cn(
          labelClasses,
          hiddenLabel && "sr-only",
          props.required && "label-required",
        )}
        htmlFor={id}
      >
        {label}
      </label>
      <div>
        <DatePicker
          dateFormat="dd/MM"
          placeholderText={placeholder}
          selected={selectedDate}
          className={cn("form-input w-full", inputClassname)}
          locale={locale}
          id={id}
          customInput={
            <input data-error={error ? "true" : undefined} type="text" />
          }
          onChange={(value) => {
            const date = Array.isArray(value) ? value[0] ?? null : value;
            setValue(name, date, { shouldValidate: true });
            setSelectedDate(date ?? null);
          }}
          data-error={error ? "true" : undefined}
          disabled={props.disabled}
          renderCustomHeader={({
            date,
            changeMonth,
            decreaseMonth,
            increaseMonth,
          }) => (
            <div className="flex items-center justify-between">
              <div className="ml-3 flex items-center gap-1">
                <button
                  type="button"
                  onClick={decreaseMonth}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white transition-colors hover:bg-yellow-300"
                >
                  <Icon
                    name={"angle-left"}
                    className="relative left-[-1px] h-2.5 w-2.5"
                  />
                </button>
                <button
                  type="button"
                  onClick={increaseMonth}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white transition-colors hover:bg-yellow-300"
                >
                  <Icon
                    name={"angle-right"}
                    className="relative left-[1px] h-2.5 w-2.5"
                  />
                </button>
              </div>
              <select
                className="shrink-0 cursor-pointer border-none bg-transparent py-0 pl-0 pr-8 text-right text-sm uppercase tracking-wider focus:ring-0"
                value={months[getMonth(date)]}
                onChange={({ target: { value } }) =>
                  changeMonth(months.indexOf(value))
                }
              >
                {months.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}
          onBlur={handleBlur}
        />
        {error && <div className="form-error basis-full">{error}</div>}
      </div>
    </InputContainer>
  );
};

export default BirthDayInput;
